import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from 'src/hooks/useDebounce'

export interface UseRouteQueryOptions {
  debounce?: number
  resetPage?: boolean
  serialize?: (value: string) => string | undefined
}

export const useRouteQuery = (
  key: string,
  defaultValue: string = '',
  options: UseRouteQueryOptions = {},
): [string, (value: string) => void] => {
  const router = useRouter()
  const { debounce = 0, resetPage = false, serialize } = options

  const [localValue, setLocalValue] = useState<string>(defaultValue)
  const debouncedValue = useDebounce(localValue, debounce)
  const isInitialMount = useRef(true)
  const isReady = router?.isReady
  const queryValue = router?.query?.[key] as string | undefined

  // 最新値をrefで保持（stale closure対策）
  const optionsRef = useRef({ key, resetPage, serialize })
  optionsRef.current = { key, resetPage, serialize }

  // URLからの初期化
  useEffect(() => {
    if (!isReady) return
    if (queryValue !== undefined) setLocalValue(queryValue)
    else setLocalValue(defaultValue)
    // URL初期化完了後にフラグをリセット
    isInitialMount.current = false
  }, [isReady, queryValue, defaultValue])

  // デバウンス後のURL更新
  useEffect(() => {
    if (!isReady) return
    if (isInitialMount.current) return

    const {
      key: currentKey,
      resetPage: currentResetPage,
      serialize: currentSerialize,
    } = optionsRef.current
    const currentValue = (router.query[currentKey] as string) || ''
    const serializedValue = currentSerialize
      ? currentSerialize(debouncedValue)
      : debouncedValue

    // 値が変更されていない場合はスキップ
    if (currentValue === (serializedValue || '')) return

    const newQuery = { ...router.query }
    if (serializedValue) newQuery[currentKey] = serializedValue
    else delete newQuery[currentKey]

    if (currentResetPage) newQuery.page = '1'

    router.push(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true },
    )
  }, [debouncedValue, isReady, router])

  return [localValue, setLocalValue]
}

export interface UseRouteQueryArrayOptions {
  resetPage?: boolean
}

export const useRouteQueryArray = (
  key: string,
  defaultValue: string[] = [],
  options: UseRouteQueryArrayOptions = {},
): [string[], (value: string[]) => void] => {
  const router = useRouter()
  const { resetPage = false } = options

  const [localValue, setLocalValue] = useState<string[]>(defaultValue)
  const isReady = router?.isReady
  const queryValue = router?.query?.[key]
  const defaultValueRef = useRef(defaultValue)

  // URLからの初期化
  useEffect(() => {
    if (!isReady) return
    if (queryValue) {
      const tagArray = Array.isArray(queryValue) ? queryValue : [queryValue]
      setLocalValue(tagArray)
    } else setLocalValue(defaultValueRef.current)
  }, [isReady, queryValue])

  // 配列値をURLに反映する関数
  const setValue = useCallback(
    (newValue: string[]) => {
      setLocalValue(newValue)

      const newQuery = { ...router.query }
      if (newValue.length > 0) newQuery[key] = newValue
      else delete newQuery[key]

      if (resetPage) newQuery.page = '1'

      router.push(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true },
      )
    },
    [router, key, resetPage],
  )

  return [localValue, setValue]
}
